import { ReactElement, useState, useCallback, useEffect } from 'react';
import { AWARD_IMAGES } from '@/common/assets/brands';
import styles from './onboarding-phone-number.module.scss';
import { UIButton, UIPhoneInput, UISpacer, UIText, UITextButton } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import isEqual from 'lodash/isEqual';
import { useRegion } from '@/common/providers/region-provider';
import { ResolvedRegion } from '@/common/providers/region-provider/models';

interface AccoladeEntry {
    src: string;
    /** Translation key suffix under `components.onboarding_phone_number`. */
    key: string;
}

const REGION_ACCOLADES: Record<ResolvedRegion, AccoladeEntry[][]> = {
    eu: [
        [
            {
                src: AWARD_IMAGES.britishExForces,
                key: 'award_ex_forces'
            },
            {
                src: AWARD_IMAGES.protectionOfLife,
                key: 'award_geovation'
            }
        ],
        [
            {
                src: AWARD_IMAGES.ukHealthAndSafetyTech,
                key: 'award_hs_tech'
            },
            {
                src: AWARD_IMAGES.techNationRisingStars,
                key: 'award_tech_nation'
            }
        ]
    ],
    us: [
        [
            {
                src: AWARD_IMAGES.exForces,
                key: 'award_ex_forces_us'
            },
            {
                src: AWARD_IMAGES.protectionOfLife,
                key: 'award_geovation'
            }
        ],
        [
            {
                src: AWARD_IMAGES.healthAndSafetyTech,
                key: 'award_hs_tech'
            },
            {
                src: AWARD_IMAGES.techNationRisingStars,
                key: 'award_tech_nation'
            }
        ]
    ]
};

type OnboardingPhoneNumberProps = {
    initialPhone: { dialCode: string; number: string } | null;
    onChange: (phone: { dialCode: string; number: string }) => void;
    onContinue: () => void;
    onBack: () => void;
};

export function OnboardingPhoneNumber(
    props: OnboardingPhoneNumberProps
): ReactElement {
    const { initialPhone, onChange, onContinue, onBack } = props;

    const { t } = useCompaniesTranslation();
    const { resolvedRegion } = useRegion();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Phone
     */
    const defaultDialCode = resolvedRegion === 'us' ? '+1' : '+44';
    const lockDialCode = resolvedRegion === 'us' ? '+1' : undefined;
    const [dialCode, setDialCode] = useState(
        initialPhone?.dialCode || defaultDialCode
    );
    const [number, setNumber] = useState(initialPhone?.number || '');
    const [isValid, setIsValid] = useState(false);

    const handleContinue = useCallback(() => {
        if (!number || isValid) {
            onContinue();
        } else {
            setShowRealTimeErrors(true);
        }
    }, [number, isValid, onContinue]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleContinue();
            }
        };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleContinue]);

    return (
        <div className={styles.container}>
            <div className={styles.firstHalf}>
                <div className={styles.content}>
                    <div>
                        <UIText variant="h1" bold>
                            {t('components.onboarding_phone_number.title')}
                        </UIText>

                        <UISpacer multiplier={1} />

                        <UIText type="default2">
                            {t('components.onboarding_phone_number.message')}.
                        </UIText>

                        <UISpacer multiplier={2} />

                        <UIPhoneInput
                            name="phoneNumber"
                            label={t('shared.fields.phone.label')}
                            initialDialCode={dialCode}
                            initialNumber={number}
                            lockDialCode={lockDialCode}
                            showRealTimeErrors={showRealTimeErrors}
                            autoFocus={true}
                            onChange={(newDialCode, newNumber, isValid) => {
                                setDialCode(newDialCode);
                                setNumber(newNumber);
                                setIsValid(isValid);

                                const phone = { dialCode, number };
                                const newPhone = {
                                    dialCode: newDialCode,
                                    number: newNumber
                                };
                                if (!isEqual(phone, newPhone)) {
                                    onChange(newPhone);
                                }
                            }}
                        />

                        <UISpacer multiplier={2} />

                        <div className={styles.buttonContainer}>
                            <UIButton
                                variant={'primary'}
                                onClick={handleContinue}
                            >
                                {t('shared.labels.continue')}
                            </UIButton>
                            <UISpacer multiplier={2} />
                            <UITextButton onClick={onBack}>
                                {t('shared.labels.back')}
                            </UITextButton>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.secondHalf}>
                <div className={styles.secondHalfContent}>
                    <UIText variant="h1" textAlign="center" type="link">
                        {t(
                            'components.onboarding_phone_number.accolades_title'
                        )}
                    </UIText>
                    <div className={styles.logos}>
                        {REGION_ACCOLADES[resolvedRegion].map(
                            (row, rowIndex) => (
                                <div key={rowIndex} className={styles.logoRow}>
                                    {row.map((accolade) => (
                                        <div
                                            key={accolade.key}
                                            className={styles.logoContainer}
                                        >
                                            <img
                                                src={accolade.src}
                                                alt={t(
                                                    `components.onboarding_phone_number.${accolade.key}`
                                                )}
                                                className={styles.logoImage}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
